import React, { useEffect, useContext } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { GlobalStateContext } from "../../controller/GlobalState";
import { blogPosts } from "../../controller/global";
import ExploreOtherCaseStudy from "../../component/ExploreOtherCaseStudy";

const QUICK_FACTS = [
  { value: "80–90%", label: "Accuracy Rate" },
  { value: "Improved", label: "Product Discovery" },
  { value: "Higher", label: "Engagement & Conversion" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the facts panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag className={className} custom={i} variants={fadeUp} initial={reduceMotion ? false : "hidden"} whileInView="show" viewport={{ once: true, amount: 0.2 }} {...rest}>
      {children}
    </MotionTag>
  );
}

const AIStyleRecommendation = () => {
  const caseStudy = blogPosts.find((post) => post.slug === "ai-style-recommendation");
  const { setIsOpen } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "AI Style Recommendation Engine: Case Study";
    const description = document.createElement("meta");
    description.name = "description";
    description.content = "How we built an AI recommendation engine for a fashion retailer — cold start, catalog turnover, and integration without replacing the storefront.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/case-studies/ai-style-recommendation");
  }, []);

  const openContactModal = () => setIsOpen(true);
  const goToCaseStudies = (e) => { e.preventDefault(); navigate("/case-studies"); };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header
        style={caseStudy?.image ? {
          backgroundImage: `linear-gradient(rgba(251,252,248,.5),rgba(251,252,248,.8)), url(${caseStudy.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        } : undefined}
      >
        <div className="ea-hero ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <a className="ea-text-link" style={{ display: "inline-block", marginBottom: 14 }} href="/case-studies" onClick={goToCaseStudies}>← All Case Studies</a>
            <p className="ea-eyebrow">Case study · {caseStudy?.category}</p>
            <h1>AI fashion recommendation engine for personalized style discovery</h1>
            <p className="ea-lede">
              Personalizing product discovery for a fashion retail platform
            </p>
            <div className="ea-actions">
              <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start your project">Start your project ↗</button>
              <a className="ea-text-link" href="/case-studies" onClick={goToCaseStudies}>Explore other case studies</a>
            </div>
          </Reveal>

          <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
            <div className="ea-engine-top"><span /> AI STYLE / QUICK FACTS <b>LIVE</b></div>
            {QUICK_FACTS.map((fact) => (
              <div className="cd-facts-row" key={fact.label}>
                <strong>{fact.value}</strong>
                <span>{fact.label}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </header>

      {/* OVERVIEW */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Overview</p>
          <h2 className="cd-h2">Overview</h2>
          <div className="std-overview">
            <p>
              A fashion retailer sought to improve how customers discovered products on their platform. Rather than relying on manual curation or basic filtering, they needed a system that could surface relevant items based on each shopper's individual behavior and preferences. We designed and integrated an AI-driven recommendation engine that worked alongside their existing platform with minimal disruption.
            </p>
          </div>
        </Reveal>
      </section>

      {/* THE CHALLENGE */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The challenge</p>
            <h2 className="cd-h2">The Challenge</h2>
            <div className="std-overview">
              <p>
                The client’s platform carried a broad and growing catalog, making it increasingly difficult for customers to find items that matched their taste. Traditional search and category browsing were leaving significant gaps in product discovery, and the retailer was seeing missed engagement opportunities as a result.
              </p>
              <p>
                The core requirement was a recommendation layer that felt natural and relevant to the individual — one that could learn from how customers interacted with the catalog over time, rather than applying generic logic across all users.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY FASHION IS HARD */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">The domain</p>
          <h2 className="cd-h2">Why Fashion Is a Hard Recommendation Problem</h2>
          <div className="std-overview">
            <p>
              Recommending films or books is a comparatively settled problem. The catalog is stable, a
              title released five years ago is still the same title, and enough people have watched it to
              infer who else will. Fashion breaks nearly every one of those assumptions.
            </p>
            <p>
              <strong>The catalog turns over constantly.</strong> A meaningful share of a fashion
              retailer&rsquo;s inventory did not exist last season and will be gone by the next. By the time an
              item has accumulated enough interactions to be confidently recommended, it may well be out
              of stock. A system that needs interaction history to work is perpetually behind the
              catalog it is meant to sell.
            </p>
            <p>
              <strong>Taste is not quality.</strong> A five-star film is broadly a good film. A dress that
              one shopper loves is one another would never wear, and neither is wrong. There is no
              universal ranking to fall back on, so popularity is a much weaker signal here than in other
              domains &mdash; and leaning on it produces a storefront where everyone sees the same
              bestsellers, which is precisely the manual-curation problem the retailer was trying to
              escape.
            </p>
            <p>
              <strong>Intent shifts within a single session.</strong> The same person shops for work
              clothes on Monday and something for a wedding on Saturday. A profile averaged across all
              their behavior describes neither. Recency and session context matter more than a
              long-run average of what someone has bought.
            </p>
            <p>
              <strong>Seasonality is not a minor adjustment.</strong> Behavior from six months ago may be
              actively misleading rather than merely stale. A model that weights all history equally will
              confidently recommend coats in July.
            </p>
          </div>
        </Reveal>
      </section>

      {/* COLD START */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">The core constraint</p>
            <h2 className="cd-h2">Cold Start, in Both Directions</h2>
            <div className="std-overview">
              <p>
                The constraint that shapes the whole design is cold start, and in retail it arrives from
                two directions at once.
              </p>
              <p>
                <strong>New items have no history.</strong> A garment added this morning has been viewed by
                nobody, so no behavioral signal exists to place it. Purely behavioral approaches simply
                cannot recommend it, which means the newest stock &mdash; usually the stock the retailer
                most wants to move &mdash; is the stock the system is worst at surfacing.
              </p>
              <p>
                <strong>New visitors have no profile.</strong> A large share of traffic to a retail site is
                first-time or logged-out. If personalization only begins after a profile exists, most
                sessions never benefit from it at all.
              </p>
              <p>
                This is why the engine draws on product catalog attributes alongside behavioral
                signals rather than behavior on its own. Attributes &mdash; category, cut, color, fabric,
                price band, and the rest of the structured data the retailer already maintained &mdash;
                describe an item from the moment it is created. A new arrival can be positioned relative
                to items a shopper has already engaged with, without waiting for anyone to interact with
                it first. In the other direction, early signals in a session narrow the field long before
                a durable profile exists.
              </p>
              <p>
                It also explains why the first phase of the work was understanding the shape of the
                client&rsquo;s data rather than selecting a model. The quality of catalog attributes sets a
                ceiling on how well anything downstream can perform. Where those attributes are sparse or
                inconsistently applied, no amount of model sophistication compensates &mdash; the system
                simply has less to reason with.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Our approach</p>
          <h2 className="cd-h2">Our Approach</h2>
          <div className="std-overview">
            <p>
              We began by working closely with the client to understand the shape of their data — what was available, how it was structured, and where the gaps were. This informed the design of a recommendation system built on proven machine learning models, tuned specifically for fashion-oriented behavior patterns.
            </p>
            <p>
              The engine was trained and fine-tuned using the brand’s own data, including product catalog attributes, historical purchase records, and aggregated customer interaction signals. Key behavioral inputs included browsing sessions, category affinity, and repeat engagement patterns — all used to model what a given customer was likely to find relevant next.
            </p>
            <p>
              Integration was handled as an extension of the existing platform rather than a replacement of any part of it. The recommendation layer was built to sit alongside the current infrastructure, exposing outputs through an API that the front-end could consume without significant rework.
            </p>
          </div>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div">
            <p className="ea-eyebrow">How it works</p>
            <h2 className="cd-h2">How It Works</h2>
            <div className="std-overview">
              <p>
                At its core, the system analyses patterns across three main dimensions: what customers view, how they navigate between categories, and what they ultimately purchase. These signals are combined to build a profile of individual style affinity, which is used to rank and filter items from the catalog in real time.
              </p>
              <p>
                The models were fine-tuned iteratively using the retailer’s product data, allowing recommendations to reflect the specific range and style vocabulary of the brand rather than applying generic fashion logic. This made the outputs feel coherent within the client’s own catalog, not like something imported from an off-the-shelf tool.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTEGRATION CONSTRAINTS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Integration constraints</p>
          <h2 className="cd-h2">Adding a Layer Without Destabilizing the Platform</h2>
          <div className="std-overview">
            <p>
              Building the engine as an extension of the existing platform rather than a replacement was
              the client&rsquo;s requirement, and it is the right instinct &mdash; a storefront that is already
              trading is not a system anyone should rebuild to add a feature. But that decision sets
              engineering constraints that shape everything downstream.
            </p>
            <p>
              <strong>Recommendations sit on the critical path of a page load.</strong> A product page that
              waits on a slow model is worse than a product page with no personalization at all. The
              recommendation layer has to answer inside the budget the page already has, which means
              deciding what can be computed ahead of time and what genuinely has to happen per request.
            </p>
            <p>
              <strong>The storefront must work when the layer does not.</strong> Any component added
              alongside a live platform has to fail invisibly. If the engine is slow, erroring, or has
              nothing confident to say about a particular shopper, the page still needs to render
              something sensible. A recommendation surface that renders empty is a visible defect; one
              that quietly falls back to a reasonable default is not.
            </p>
            <p>
              <strong>An API boundary is what makes the layer replaceable.</strong> Exposing outputs through
              an API the front end consumes meant the storefront needed no significant rework, and it
              means the model behind that boundary can be retrained, tuned or swapped without the
              storefront knowing. In a domain where seasonality alone guarantees the model will need
              revisiting, that separation is not architectural neatness &mdash; it is what makes the system
              maintainable past its first season.
            </p>
          </div>
        </Reveal>
      </section>

      {/* OUTCOME */}
      <section className="ea-wrap ea-section">
        <Reveal as="div">
          <p className="ea-eyebrow">Outcome</p>
          <h2 className="cd-h2">Outcome</h2>
          <div className="std-overview">
            <p>
              The recommendation system achieved an accuracy rate in the range of 80–90%, a meaningful result given the breadth of the catalog and the diversity of customer behavior. Recommendations became noticeably more relevant, reducing the effort required for customers to find items aligned with their preferences.
            </p>
            <p>
              Product discovery improved across the platform, with customers engaging more consistently with suggested items. The retailer also saw a positive impact on conversion potential, as better-matched recommendations brought more intent-aligned products into the customer journey at the right moment.
            </p>
          </div>
        </Reveal>
      </section>

      {/* EXPLORE OTHER CASE STUDIES */}
      <ExploreOtherCaseStudy currentSlug="ai-style-recommendation" />

      {/* CTA */}
      <section className="ea-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Ready when you are</div>
            <h2 style={{ margin: "16px 0" }}>Ready to Personalize Your Customer Experience?</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              Let's discuss how AI can help you deliver smarter, more relevant product recommendations and boost engagement.
            </p>
            <button className="ea-button ea-primary" onClick={openContactModal}>Get in Touch</button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default AIStyleRecommendation;
